import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { collection, collectionGroup, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { lokalDatum, uppfoljningStatusText } from '../modules/crm/pipelineFlode';
import {
  Box, Container, Typography, Paper, IconButton, Chip,
  CircularProgress, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import EventIcon from '@mui/icons-material/Event';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LagbasLogo from '../components/LagbasLogo';

function formatDate(d) {
  if (!d) return '–';
  return new Date(d + 'T00:00:00').toLocaleDateString('sv-SE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = Math.round((new Date(dateStr + 'T00:00:00') - new Date(new Date().toDateString())) / 86400000);
  return diff;
}

export default function CRMReminders() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    load();
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Påminnelserna ligger på leadet (nextFollowUpReminder, mottagare
  // nextFollowUpReminderUid). Pilotpåminnelserna är egna automatiska
  // aktiviteter, se pilotPaminnelser.js.
  async function load() {
    setLoading(true);
    try {
      const today = lokalDatum();
      const [leadSnap, aktSnap] = await Promise.all([
        getDocs(query(collection(db, 'crmLeads'), where('nextFollowUpReminderUid', '==', currentUser.uid))),
        getDocs(query(collectionGroup(db, 'activities'), where('createdBy', '==', currentUser.uid))),
      ]);

      const franLeads = leadSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(l => l.nextFollowUpReminder && l.nextFollowUp >= today)
        .map(l => ({
          id: `lead-${l.id}`, kalla: 'lead', leadId: l.id, lead: l,
          followUpDate: l.nextFollowUp, followUpTime: l.nextFollowUpTime || '',
          summary: [l.nastaSteg, uppfoljningStatusText(l)].filter(Boolean).join(' · '),
        }));

      const pilot = aktSnap.docs
        .map(d => ({ id: d.id, kalla: 'pilot', leadId: d.ref.parent.parent.id, ...d.data() }))
        .filter(a => a.automatisk && a.reminderEnabled && a.followUpDate >= today);
      const leadMap = {};
      await Promise.all([...new Set(pilot.map(a => a.leadId))].map(async lid => {
        const ls = await getDoc(doc(db, 'crmLeads', lid));
        if (ls.exists()) leadMap[lid] = ls.data();
      }));

      const alla = [...franLeads, ...pilot.map(a => ({ ...a, lead: leadMap[a.leadId] || null }))];
      alla.sort((a, b) => a.followUpDate.localeCompare(b.followUpDate));
      setReminders(alla);
    } catch (e) {
      console.error('CRMReminders load error:', e);
    } finally {
      setLoading(false);
    }
  }

  async function handleDisable(reminder) {
    if (reminder.kalla === 'lead') {
      await updateDoc(doc(db, 'crmLeads', reminder.leadId), { nextFollowUpReminder: false, nextFollowUpReminderUid: '' });
    } else {
      await updateDoc(doc(db, 'crmLeads', reminder.leadId, 'activities', reminder.id), {
        reminderEnabled: false,
        reminderDate: '',
      });
    }
    setReminders(prev => prev.filter(r => r.id !== reminder.id));
  }

  const today = lokalDatum();

  const grouped = reminders.reduce((acc, r) => {
    const d = daysUntil(r.followUpDate);
    const key = d < 0 ? 'overdue' : d === 0 ? 'today' : d <= 7 ? 'thisweek' : 'later';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  const GROUPS = [
    { key: 'overdue',  label: 'Försenade',        color: '#C62828', bg: '#FFEBEE' },
    { key: 'today',    label: 'Idag',              color: '#E65100', bg: '#FFF3E0' },
    { key: 'thisweek', label: 'Denna vecka',       color: '#1565C0', bg: '#E3F2FD' },
    { key: 'later',    label: 'Längre fram',       color: '#444',    bg: '#F5F5F5' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #eee', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo size={32} />
        <IconButton onClick={() => navigate('/crm/leads')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <NotificationsActiveIcon sx={{ color: '#FF6B00' }} />
          <Typography variant="h6" fontWeight="bold">Mina påminnelser</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Chip
          label={`${reminders.length} aktiva`}
          size="small"
          sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600 }}
        />
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress sx={{ color: '#FF6B00' }} />
          </Box>
        ) : reminders.length === 0 ? (
          <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
            <NotificationsOffIcon sx={{ fontSize: 48, color: '#ddd', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">Inga aktiva påminnelser</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Slå på påminnelse via e-post under Uppföljning på ett lead.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {GROUPS.map(group => {
              const items = grouped[group.key];
              if (!items?.length) return null;
              return (
                <Box key={group.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: group.color }} />
                    <Typography variant="subtitle2" fontWeight={700} color={group.color}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.72rem' }}>
                      {group.label}
                    </Typography>
                    <Chip label={items.length} size="small"
                      sx={{ height: 18, fontSize: '0.68rem', bgcolor: group.bg, color: group.color, fontWeight: 700 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {items.map(r => {
                      const d = daysUntil(r.followUpDate);
                      const leadTitle = r.lead?.title || 'Lead';
                      return (
                        <Paper key={r.id} elevation={0} sx={{
                          border: '1px solid',
                          borderColor: r.followUpDate === today ? '#FFD0B0' : '#E0E0E0',
                          borderRadius: 2,
                          p: 2.5,
                          bgcolor: r.followUpDate === today ? '#FFF8F5' : '#fff',
                          display: 'flex', alignItems: 'flex-start', gap: 2,
                        }}>
                          <Box sx={{
                            width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                            bgcolor: d < 0 ? '#FFEBEE' : d === 0 ? '#FFF3E0' : '#F5F5F5',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <EventIcon sx={{ fontSize: 18, color: d < 0 ? '#C62828' : d === 0 ? '#E65100' : '#888' }} />
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: d < 0 ? '#C62828' : d === 0 ? '#E65100' : '#888', fontWeight: 700, lineHeight: 1 }}>
                              {d < 0 ? `${Math.abs(d)}d` : d === 0 ? 'idag' : `${d}d`}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                              <Typography variant="body1" fontWeight={700} noWrap>{leadTitle}</Typography>
                              {r.contactName && (
                                <Typography variant="body2" color="text.secondary" noWrap>– {r.contactName}</Typography>
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">{formatDate(r.followUpDate)}{r.followUpTime ? ` kl. ${r.followUpTime}` : ''}</Typography>
                            {r.summary && (
                              <Typography variant="body2" color="text.secondary"
                                sx={{ mt: 0.75, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                {r.summary}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            <Tooltip title="Öppna lead">
                              <IconButton size="small" onClick={() => navigate(`/crm/leads/${r.leadId}`)}>
                                <OpenInNewIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Stäng av påminnelse">
                              <IconButton size="small" onClick={() => handleDisable(r)}>
                                <NotificationsOffIcon sx={{ fontSize: 18, color: '#bbb' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
}
